import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import isFunction from 'lodash/isFunction';

import { UiModal } from 'src/components/UI/modal/UiModal';
import { useNsTHelper } from 'src/utils/i18n';
import { PlusSubscriptionHeader } from 'src/components/plusSubscription/modals/components/Header';
import { PlusSubscriptionContentHeader } from 'src/components/plusSubscription/modals/components/Modal/ContentHeader';
import { PlusSubscriptionFeatures } from 'src/components/plusSubscription/modals/components/Modal/Features';
import { PlusSubscriptionButtons } from 'src/components/plusSubscription/modals/components/Modal/Buttons';
import { apiContinueWithBasicSubscription } from 'src/api/plusSubscriptionPlanSelection';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';
import { hardRedirect } from 'src/utils/hardRedirect';
import { dispatchAnyRequestStart } from 'src/store/anyRequest/helpers';
import { PlusSubscriptionSliceStateType } from 'src/store/plusSubscription/slice';
import { closePlusSubscriptionModal } from 'src/store/plusSubscription/helpers';
import {
  PlusSubscriptionHeaderVisibleSelector,
  PlusSubscriptionModalInfoLoadingSelector,
  PlusSubscriptionModalSelector,
} from 'src/store/plusSubscription/selectors';
import styles from 'src/styles/common/plusSubscription/modal/modal.module.scss';
import { createKustomerConversation } from 'src/utils/outer';
import { UpsellUpgradeNowModal } from 'src/components/plusSubscription/modals/UpgradeNow';
import { UpsellUnlimitedModal } from 'src/components/plusSubscription/modals/Unlimited';
import { UpsellPlanPreselected } from 'src/components/plusSubscription/modals/PlanPreselected';

type Props = {
  type: Extract<
    PlusSubscriptionSliceStateType['modal']['type'],
    'planSelection' | 'trialExpired' | 'upgradeNow' | 'unlimited' | 'planPreselected'
  >;
};

export const PlusSubscriptionModal: FC<Props> = ({ type }) => {
  const tHelperConfirm = useNsTHelper('plusSubscription', 'modals.confirm.common');

  const [showUpsellModal, setShowUpsellModal] = useState(true);

  const showPrevModal = useCallback(() => {
    setShowUpsellModal(true);
  }, []);

  /** confirm */
  const [showConfirm, setShowConfirm] = useState(false);

  const uniqueId = 'continueWithRallyUpNow';

  const continueWithBasicSubscription = useCallback(() => {
    dispatchAnyRequestStart({
      data: null,
      apiFunction: apiContinueWithBasicSubscription,
      uniqueId,
    });
  }, []);

  const { loading } = useAnyRequest(uniqueId, {
    onSuccess: () => {
      hardRedirect('/');
    },
  });
  /** end confirm */

  const { closeCallback, canClose } = useSelector(PlusSubscriptionModalSelector);

  const hideModal = useCallback(() => {
    if (isFunction(closeCallback)) {
      closeCallback();
      return;
    }

    if (type === 'trialExpired') {
      continueWithBasicSubscription();
    }

    closePlusSubscriptionModal();
  }, [closeCallback, continueWithBasicSubscription, type]);

  const handleCloseModal = useCallback(() => {
    if (canClose || type === 'unlimited') {
      hideModal();
      return;
    }

    setShowConfirm(true);

    setShowUpsellModal(false);
  }, [canClose, hideModal, type]);

  const handleContactUsClick = useCallback(() => {
    const ASSISTANT_ID = '663155ba28a0354608d95d65';

    createKustomerConversation({ assistantId: ASSISTANT_ID });
  }, []);

  const isLoadingPlans = useSelector(PlusSubscriptionModalInfoLoadingSelector);
  const showHeader = useSelector(PlusSubscriptionHeaderVisibleSelector);

  return (
    <>
      {showUpsellModal && (
        <>
          {type === 'upgradeNow' && (
            <UpsellUpgradeNowModal
              onClose={handleCloseModal}
              onContactUsClick={handleContactUsClick}
              showHeader={showHeader}
            />
          )}

          {type === 'planPreselected' && <UpsellPlanPreselected onClose={handleCloseModal} />}

          {type === 'unlimited' && <UpsellUnlimitedModal onClose={handleCloseModal} />}

          {(type === 'trialExpired' || type === 'planSelection') && (
            <UiModal
              noFooter
              onHide={handleCloseModal}
              modalClassName={classNames(styles.modal)}
              loading={isLoadingPlans}
            >
              <PlusSubscriptionHeader onClose={handleCloseModal} type={type} showHeader={showHeader} />
              <div className={styles.content}>
                <PlusSubscriptionContentHeader type={type} />
                <PlusSubscriptionFeatures />
                <PlusSubscriptionButtons
                  type={type}
                  onContactUsClick={handleContactUsClick}
                  onClose={handleCloseModal}
                />
              </div>
            </UiModal>
          )}
        </>
      )}

      {showConfirm && (
        <UiModal
          disableClose
          header={tHelperConfirm('header')}
          cancelBtnText={tHelperConfirm('buttons.back')}
          successBtnText={tHelperConfirm('buttons.continue')}
          cancelBtnOnClick={showPrevModal}
          successBtnOnClick={continueWithBasicSubscription}
          successLoading={loading}
        >
          {tHelperConfirm('body')}
        </UiModal>
      )}
    </>
  );
};
